import type { EncodeObject } from '@cosmjs/proto-signing';

/**
 * Create a delegate message for staking tokens to a validator
 */
export function createDelegateMsg(
  delegatorAddress: string,
  validatorAddress: string,
  amount: string,
  denom = 'ugraviton'
): EncodeObject {
  return {
    typeUrl: '/cosmos.staking.v1beta1.MsgDelegate',
    value: {
      delegatorAddress,
      validatorAddress,
      amount: {
        denom,
        amount,
      },
    },
  };
}

/**
 * Create an undelegate message for unstaking tokens from a validator
 */
export function createUndelegateMsg(
  delegatorAddress: string,
  validatorAddress: string,
  amount: string,
  denom = 'ugraviton'
): EncodeObject {
  return {
    typeUrl: '/cosmos.staking.v1beta1.MsgUndelegate',
    value: {
      delegatorAddress,
      validatorAddress,
      amount: {
        denom,
        amount,
      },
    },
  };
}

/**
 * Create a redelegate message for moving stake from one validator to another
 */
export function createRedelegateMsg(
  delegatorAddress: string,
  validatorSrcAddress: string,
  validatorDstAddress: string,
  amount: string,
  denom = 'ugraviton'
): EncodeObject {
  return {
    typeUrl: '/cosmos.staking.v1beta1.MsgBeginRedelegate',
    value: {
      delegatorAddress,
      validatorSrcAddress,
      validatorDstAddress,
      amount: {
        denom,
        amount,
      },
    },
  };
}

/**
 * Create a claim rewards message for withdrawing rewards from a validator
 */
export function createClaimRewardsMsg(
  delegatorAddress: string,
  validatorAddress: string
): EncodeObject {
  return {
    typeUrl: '/cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward',
    value: {
      delegatorAddress,
      validatorAddress,
    },
  };
}

/**
 * Create multiple claim rewards messages for withdrawing from multiple validators
 */
export function createClaimAllRewardsMsg(
  delegatorAddress: string,
  validatorAddresses: string[]
): EncodeObject[] {
  return validatorAddresses.map((validatorAddress) =>
    createClaimRewardsMsg(delegatorAddress, validatorAddress)
  );
}

/**
 * Convert human-readable token amount to micro-units (1 GRAV = 1,000,000 ugraviton)
 */
export function toMicroAmount(amount: string | number): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num) || num < 0) {
    throw new Error('Invalid amount');
  }
  return Math.floor(num * 1_000_000).toString();
}

/**
 * Convert micro-units to human-readable token amount
 */
export function fromMicroAmount(amount: string | number): number {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num) || num < 0) {
    return 0;
  }
  return num / 1_000_000;
}
